import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 3
import { PanelFrame } from '../workspace/PanelFrame';
import { Layers, Compass, PenLine, Wand2, Globe2, Check, Save, FolderOpen, Trash2, Cloud, CloudOff, Share2, ClipboardPaste, Copy, Sparkles } from 'lucide-react';
import { usePW } from '@/lib/store';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';

const GLYPHS = { Compass, PenLine, Wand2, Globe2 };
const PRESETS_KEY = 'pw_mode_presets_v1';
const SCOPE_KEY   = 'pw_mode_scope_v1';
const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
// iter55g3 — Shareable preset codes. Format: `pw1:<base64url(json)>`.
// The `pw1:` prefix versions the encoding so future breaking changes
// (e.g. compression) can bump to `pw2:` without corrupting old codes.
const SHARE_PREFIX = 'pw1:';

function b64urlEncode(s) {
  // btoa handles latin-1 only; guard against wide chars by URI-encoding first.
  const b64 = typeof btoa === 'function'
    ? btoa(unescape(encodeURIComponent(s)))
    : Buffer.from(s, 'utf8').toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function b64urlDecode(s) {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const raw = typeof atob === 'function'
    ? atob(b64)
    : Buffer.from(b64, 'base64').toString('binary');
  return decodeURIComponent(escape(raw));
}
function encodePresetCode(preset) {
  // Trim to just the shareable fields so the code stays short.
  const payload = {
    n: preset.name || '',
    a: preset.activeMode || null,
    t: preset.toggles || {},
    v: 1,
  };
  return SHARE_PREFIX + b64urlEncode(JSON.stringify(payload));
}
function decodePresetCode(code) {
  const raw = (code || '').trim();
  if (!raw.startsWith(SHARE_PREFIX)) {
    throw new Error('Not a The Loom preset code (missing "pw1:" prefix).');
  }
  const json = b64urlDecode(raw.slice(SHARE_PREFIX.length));
  const parsed = JSON.parse(json);
  if (!parsed || typeof parsed !== 'object' || !parsed.t) {
    throw new Error('Preset code is malformed.');
  }
  return {
    name:       String(parsed.n || 'Shared preset').slice(0, 80),
    activeMode: parsed.a || null,
    toggles:    parsed.t || {},
  };
}
async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

function loadPresets() {
  try { return JSON.parse(localStorage.getItem(PRESETS_KEY) || '{}'); }
  catch { return {}; }
}
function savePresets(p) {
  try { localStorage.setItem(PRESETS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

// iter55g2 — Sync helpers. Local writes are authoritative for the current
// browser; backend sync is best-effort so an offline user can still
// save/load. Server presets shape: {id, name, active_mode, toggles, ...}.
async function fetchServerPresets() {
  try {
    const r = await axios.get(`${API}/mode-presets`, { withCredentials: true });
    return Array.isArray(r.data) ? r.data : [];
  } catch { return null; }
}
async function pushServerPreset(name, payload) {
  try {
    const r = await axios.post(`${API}/mode-presets`, {
      name,
      active_mode: payload.activeMode || null,
      toggles: payload.toggles || {},
    }, { withCredentials: true });
    return r.data;
  } catch { return null; }
}
async function deleteServerPresetById(id) {
  try {
    await axios.delete(`${API}/mode-presets/${id}`, { withCredentials: true });
    return true;
  } catch { return false; }
}

export function ModesPanel({ onClose, embedded }) {
  const { modes, activeMode, setActiveMode, toggles, setToggle } = usePW();
  const [scopeByMode, setScopeByMode] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SCOPE_KEY) || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(SCOPE_KEY, JSON.stringify(scopeByMode)); }
    catch { /* ignore */ }
  }, [scopeByMode]);
  const setScope = (modeId, scope) => setScopeByMode((s) => ({ ...s, [modeId]: scope }));
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  // iter55g3 — Share/Import Preset Code (viral loop for Community Marketplace).
  const [importOpen, setImportOpen] = useState(false);
  const [importCode, setImportCode] = useState('');
  const [importPreview, setImportPreview] = useState(null);   // {name, activeMode, toggles} | null
  const [importError, setImportError] = useState('');
  // iter55g2 — id map for remote presets keyed by name (so delete can
  // reach the server) + a "synced" indicator for the badge.
  const [serverIds, setServerIds] = useState({});   // { [name]: server_id }
  const [syncStatus, setSyncStatus] = useState('idle');   // 'idle'|'syncing'|'synced'|'offline'

  // On mount, pull server presets and merge into localStorage. Newest wins
  // per name (compares updated_at ISO strings; missing local rows are
  // hydrated from the server so a new browser sees them immediately).
  useEffect(() => {
    let cancel = false;
    (async () => {
      setSyncStatus('syncing');
      const rows = await fetchServerPresets();
      if (cancel) return;
      if (rows === null) { setSyncStatus('offline'); return; }
      const local = loadPresets();
      const ids = {};
      for (const row of rows) {
        ids[row.name] = row.id;
        const l = local[row.name];
        const remoteTs = row.updated_at || '';
        const localTs  = (l && l.savedAt) || '';
        if (!l || remoteTs > localTs) {
          local[row.name] = {
            toggles:    row.toggles || {},
            activeMode: row.active_mode || null,
            savedAt:    row.updated_at || new Date().toISOString(),
          };
        }
      }
      savePresets(local);
      setServerIds(ids);
      setSyncStatus('synced');
    })();
    return () => { cancel = true; };
  }, []);

  const doSavePreset = async () => {
    const nm = (newPresetName || '').trim();
    if (!nm) { toast.error('Give the preset a name.'); return; }
    const payload = { toggles: { ...toggles }, activeMode, savedAt: new Date().toISOString() };
    const all = loadPresets();
    all[nm] = payload;
    savePresets(all);
    setSaveOpen(false); setNewPresetName('');
    toast.success(`Saved preset "${nm}".`);
    // Best-effort push to backend.
    setSyncStatus('syncing');
    const remote = await pushServerPreset(nm, payload);
    if (remote && remote.id) {
      setServerIds((m) => ({ ...m, [nm]: remote.id }));
      setSyncStatus('synced');
    } else {
      setSyncStatus('offline');
    }
  };
  const doLoadPreset = (nm) => {
    const all = loadPresets();
    const p = all[nm];
    if (!p) { toast.error('Preset not found.'); return; }
    for (const [k, v] of Object.entries(p.toggles || {})) { try { setToggle(k, v); } catch { /* ignore */ } }
    if (p.activeMode) setActiveMode(p.activeMode);
    toast.success(`Loaded preset "${nm}".`);
    setLoadOpen(false);
  };
  const doDeletePreset = async (nm) => {
    const all = loadPresets();
    delete all[nm];
    savePresets(all);
    toast.success(`Deleted preset "${nm}".`);
    // Best-effort remote delete.
    const rid = serverIds[nm];
    if (rid) {
      await deleteServerPresetById(rid);
      setServerIds((m) => { const c = { ...m }; delete c[nm]; return c; });
    }
    // Force refresh of the list
    setLoadOpen(false); setTimeout(() => setLoadOpen(true), 0);
  };

  // iter55g3 — Copy a shareable code for a saved preset.
  const doSharePreset = async (nm) => {
    const all = loadPresets();
    const p = all[nm];
    if (!p) { toast.error('Preset not found.'); return; }
    const code = encodePresetCode({ name: nm, activeMode: p.activeMode, toggles: p.toggles });
    const ok = await copyToClipboard(code);
    if (ok) {
      toast.success(`Copied "${nm}" share code`, {
        description: 'Paste this into a chat, tweet or forum post — anyone with The Loom can import it in one click.',
      });
    } else {
      toast.error('Could not copy — long-press the box below to copy manually.',
                  { description: code });
    }
  };

  // iter55g3 — Preview an incoming code as the user types / pastes.
  const previewImport = (raw) => {
    setImportCode(raw);
    if (!raw.trim()) { setImportPreview(null); setImportError(''); return; }
    try {
      const p = decodePresetCode(raw);
      setImportPreview(p);
      setImportError('');
    } catch (e) {
      setImportPreview(null);
      setImportError(e.message || 'Could not decode preset code.');
    }
  };

  const doImportPreset = async () => {
    if (!importPreview) { toast.error(importError || 'Paste a valid pw1: code first.'); return; }
    let nm = importPreview.name || 'Shared preset';
    const all = loadPresets();
    // Avoid overwriting an existing preset with the same name.
    if (all[nm]) {
      let i = 2;
      while (all[`${nm} (${i})`]) i += 1;
      nm = `${nm} (${i})`;
    }
    const payload = {
      toggles:    importPreview.toggles || {},
      activeMode: importPreview.activeMode || null,
      savedAt:    new Date().toISOString(),
    };
    all[nm] = payload;
    savePresets(all);
    // Apply immediately so the writer can hear it working.
    for (const [k, v] of Object.entries(payload.toggles)) {
      try { setToggle(k, v); } catch { /* ignore */ }
    }
    if (payload.activeMode) setActiveMode(payload.activeMode);
    toast.success(`Imported preset "${nm}"`, {
      description: 'Applied to your dials and saved to your library.',
    });
    // Best-effort backend sync.
    setSyncStatus('syncing');
    const remote = await pushServerPreset(nm, payload);
    if (remote && remote.id) {
      setServerIds((m) => ({ ...m, [nm]: remote.id }));
      setSyncStatus('synced');
    } else {
      setSyncStatus('offline');
    }
    setImportOpen(false); setImportCode(''); setImportPreview(null); setImportError('');
  };

  const Wrapper = embedded
    ? ({ children }) => <div className="h-full overflow-auto scroll-thin panel-bg">{children}</div>
    : ({ children }) => (
        <PanelFrame title="Editing Modes" icon={Layers} onClose={onClose}>
          <div className="h-full overflow-auto scroll-thin">{children}</div>
        </PanelFrame>
      );

  return (
    <Wrapper>
      <div className="grid grid-cols-1 gap-3 p-3 md:grid-cols-2 xl:grid-cols-4">
        {modes.map((m) => {
          const Glyph = GLYPHS[m.glyph] || Layers;
          const active = activeMode === m.id;
          const scope = scopeByMode[m.id] || 'entire';
          return (
            <div
              key={m.id}
              role="button"
              tabIndex={0}
              onClick={() => setActiveMode(m.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveMode(m.id); } }}
              data-testid={`modes-card-${m.id}`}
              className={cn(
                'group relative flex flex-col rounded-md border bg-card p-3 text-left transition-all cursor-pointer',
                'hover:-translate-y-0.5 hover:shadow-panel-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                active ? 'border-primary shadow-panel-active' : 'border-panel-border shadow-panel'
              )}
            >
              <div className="flex items-start justify-between">
                <div className={cn('flex h-9 w-9 items-center justify-center rounded-md border', `border-${m.accent}/30`, `bg-${m.accent}/10`)}>
                  <Glyph className={cn('h-4 w-4', `text-${m.accent}`)} strokeWidth={2.25} />
                </div>
                {active && (
                  <span className="flex items-center gap-1 rounded-sm bg-primary px-1.5 py-0.5 text-[13px] font-semibold uppercase tracking-wider text-primary-foreground">
                    <Check className="h-3 w-3" /> Active
                  </span>
                )}
              </div>
              <div className="mt-3">
                <div className="text-[13px] uppercase tracking-[0.18em] text-muted-foreground">Mode</div>
                <h3 className="mt-0.5 text-[15px] font-semibold tracking-tight">{m.title}</h3>
                <p className="mt-1 text-[13.5px] leading-relaxed text-muted-foreground">{m.goal}</p>
              </div>
              <ul className="mt-3 flex flex-wrap gap-1">
                {m.keys.slice(0, 5).map(k => (
                  <li key={k} className="rounded-sm bg-secondary px-1.5 py-0.5 text-[13px] text-muted-foreground">{k}</li>
                ))}
                {m.keys.length > 5 && <li className="rounded-sm bg-secondary px-1.5 py-0.5 text-[13px] text-muted-foreground">+{m.keys.length - 5}</li>}
              </ul>
              <div className="mt-3 flex items-center gap-1 text-[13px] text-muted-foreground">
                <span>Apply to:</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setScope(m.id, 'current'); }}
                  data-testid={`modes-scope-${m.id}-current`}
                  className={cn('rounded-sm border px-1.5 py-0.5 text-[13px]',
                    scope === 'current'
                      ? 'border-primary bg-primary/15 text-primary'
                      : 'border-border bg-background hover:bg-secondary')}
                >Current chapter</button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setScope(m.id, 'entire'); }}
                  data-testid={`modes-scope-${m.id}-entire`}
                  className={cn('rounded-sm border px-1.5 py-0.5 text-[13px]',
                    scope === 'entire'
                      ? 'border-primary bg-primary/15 text-primary'
                      : 'border-border bg-background hover:bg-secondary')}
                >Entire draft</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Global toggle row */}
      <div className="m-3 mt-0 rounded-md border border-panel-border bg-card p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[13px] uppercase tracking-[0.18em] text-muted-foreground">Universal · Applied to all modes</div>
          <div className="flex items-center gap-1">
            <span
              data-testid={`modes-sync-status-${syncStatus}`}
              className={cn(
                'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[9.5px] font-medium uppercase tracking-wider',
                syncStatus === 'synced'  && 'bg-emerald-100 text-emerald-700',
                syncStatus === 'syncing' && 'bg-primary/10 text-primary',
                syncStatus === 'offline' && 'bg-amber-100 text-amber-800',
                syncStatus === 'idle'    && 'bg-secondary text-muted-foreground',
              )}
              title={
                syncStatus === 'synced' ? 'Presets synced across your devices'
                : syncStatus === 'syncing' ? 'Syncing with the cloud…'
                : syncStatus === 'offline' ? 'Cloud sync unavailable — presets saved locally only'
                : 'Idle'
              }
            >
              {syncStatus === 'offline'
                ? <><CloudOff className="h-2.5 w-2.5" /> Local only</>
                : <><Cloud className="h-2.5 w-2.5" /> {syncStatus === 'synced' ? 'Synced' : (syncStatus === 'syncing' ? 'Syncing…' : 'Cloud')}</>}
            </span>
            <Button variant="ghost" size="sm"
                    className="h-6 gap-1 px-1.5 text-[13px]"
                    onClick={() => setSaveOpen(true)}
                    data-testid="modes-save-preset">
              <Save className="h-3 w-3" /> Save Preset
            </Button>
            <Button variant="ghost" size="sm"
                    className="h-6 gap-1 px-1.5 text-[13px]"
                    onClick={() => setLoadOpen(true)}
                    data-testid="modes-load-preset">
              <FolderOpen className="h-3 w-3" /> Load…
            </Button>
            <Button variant="ghost" size="sm"
                    className="h-6 gap-1 px-1.5 text-[13px]"
                    onClick={() => { setImportOpen(true); setImportCode(''); setImportPreview(null); setImportError(''); }}
                    data-testid="modes-import-preset-code"
                    title="Paste a shared preset code (pw1:…) to import it">
              <ClipboardPaste className="h-3 w-3" /> Import code…
            </Button>
          </div>
        </div>
        <GlobalToggleRow />
      </div>

      {/* Save preset dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm" data-testid="modes-save-preset-dialog">
          <DialogHeader>
            <DialogTitle>Save preset</DialogTitle>
            <DialogDescription className="text-[13.5px]">
              Snapshot your current dials (mode, tense, POV, sliders) under a
              memorable name — you can load it later from any of your devices.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newPresetName}
            onChange={(e) => setNewPresetName(e.target.value)}
            placeholder='e.g. "Contemporary Romance · Elena"'
            data-testid="modes-save-preset-name"
            onKeyDown={(e) => { if (e.key === 'Enter') doSavePreset(); }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={doSavePreset} data-testid="modes-save-preset-confirm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load preset dialog */}
      <Dialog open={loadOpen} onOpenChange={setLoadOpen}>
        <DialogContent className="max-w-md" data-testid="modes-load-preset-dialog">
          <DialogHeader>
            <DialogTitle>Saved presets</DialogTitle>
            <DialogDescription className="text-[13.5px]">
              Load a preset onto your dials, copy a share code to send to another
              writer, or delete presets you no longer need.
            </DialogDescription>
          </DialogHeader>
          <PresetList onLoad={doLoadPreset} onDelete={doDeletePreset} onShare={doSharePreset} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLoadOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter55g3 — Import shared preset code dialog. */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md" data-testid="modes-import-preset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-4 w-4 text-primary" /> Import shared preset
            </DialogTitle>
            <DialogDescription className="text-[13.5px]">
              Paste a <code className="text-[13px]">pw1:</code> code shared by another writer.
              We&apos;ll decode the dials, apply them to your workspace, and save the
              preset to your library.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={importCode}
              onChange={(e) => previewImport(e.target.value)}
              placeholder="pw1:eyJuIjoiQ29udGVtcG9yYXJ5IFJvbWFuY2Ug…"
              rows={3}
              data-testid="modes-import-preset-textarea"
              className="font-mono text-[13px]"
            />
            {importError && (
              <div className="rounded-sm border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-[13px] text-destructive"
                   data-testid="modes-import-preset-error">
                {importError}
              </div>
            )}
            {importPreview && (
              <div className="rounded-md border border-primary/30 bg-primary/5 p-2 text-[13.5px]"
                   data-testid="modes-import-preset-preview">
                <div className="font-semibold">{importPreview.name}</div>
                <div className="mt-1 text-[13px] text-muted-foreground">
                  Mode: {importPreview.activeMode || '—'}
                  {' · '}
                  {Object.entries(importPreview.toggles || {})
                    .slice(0, 5)
                    .map(([k, v]) => `${k}=${v}`).join(', ')}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button
              onClick={doImportPreset}
              disabled={!importPreview}
              data-testid="modes-import-preset-confirm"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Apply &amp; save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Wrapper>
  );
}

function PresetList({ onLoad, onDelete, onShare }) {
  const [presets, setPresets] = useState(loadPresets);
  useEffect(() => { setPresets(loadPresets()); }, []);
  const entries = Object.entries(presets);
  if (entries.length === 0) {
    return <div className="rounded-md border border-dashed p-6 text-center text-[13.5px] text-muted-foreground"
                data-testid="modes-load-preset-empty">
      No presets saved yet. Click <b>Save Preset</b> to snapshot your current dials.
    </div>;
  }
  return (
    <ul className="scroll-thin max-h-[50vh] space-y-1 overflow-y-auto" data-testid="modes-load-preset-list">
      {entries.map(([name, p]) => (
        <li key={name} className="flex items-center gap-2 rounded-md border border-panel-border bg-card p-2 hover:bg-secondary">
          <button type="button"
                   onClick={() => onLoad(name)}
                   data-testid={`modes-preset-load-${name.replace(/\s+/g, '-')}`}
                   className="flex-1 text-left">
            <div className="text-[14px] font-semibold">{name}</div>
            <div className="text-[13px] text-muted-foreground">
              {p.activeMode ? `Mode: ${p.activeMode}` : 'Mode: —'} · Saved {p.savedAt ? new Date(p.savedAt).toLocaleDateString() : '—'}
            </div>
          </button>
          <button type="button"
                   onClick={() => onShare && onShare(name)}
                   data-testid={`modes-preset-share-${name.replace(/\s+/g, '-')}`}
                   className="rounded-sm p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                   title="Copy share code">
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button type="button"
                   onClick={() => onDelete(name)}
                   data-testid={`modes-preset-delete-${name.replace(/\s+/g, '-')}`}
                   className="rounded-sm p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                   title="Delete preset">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function GlobalToggleRow() {
  const { toggles, setToggle, togglesMeta } = usePW();
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
      <Segmented label="Tense"   options={togglesMeta.tense} value={toggles.tense} onChange={v => setToggle('tense', v)} />
      <Segmented label="POV"     options={togglesMeta.pov}   value={toggles.pov}   onChange={v => setToggle('pov', v)} />
      <Dial label="Surprise me"  value={toggles.surprise} onChange={v => setToggle('surprise', v)} from="safe" to="chaotic" />
      <Dial label="Heart"        value={toggles.heart}    onChange={v => setToggle('heart', v)}    from="cold" to="emotional" />
      <Dial label="Prose density" value={toggles.density} onChange={v => setToggle('density', v)} from="sparse" to="dense" />
    </div>
  );
}

function Segmented({ label, options, value, onChange }) {
  return (
    <div>
      <div className="mb-1 text-[13px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="flex rounded-md border border-border bg-background p-0.5">
        {options.map(o => (
          <button key={o} onClick={() => onChange(o)} className={cn('flex-1 rounded-sm px-1.5 py-1 text-[13px] font-medium transition-colors', value === o ? 'bg-primary text-primary-foreground shadow-inset-top' : 'text-muted-foreground hover:bg-secondary')}>{o}</button>
        ))}
      </div>
    </div>
  );
}

function Dial({ label, value, onChange, from, to }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[13px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="font-mono-stat text-[13px] text-foreground">{value}%</span>
      </div>
      <Slider value={[value]} onValueChange={([v]) => onChange(v)} min={0} max={100} step={1} className="py-1" />
      <div className="mt-0.5 flex justify-between text-[9.5px] uppercase tracking-wider text-muted-foreground/70"><span>{from}</span><span>{to}</span></div>
    </div>
  );
}
